export const SITE = {
  name: 'New Leaf Cleaning',
  tagline: 'Breathing life back into your home, one clean at a time.',
  taglineSub: 'Naturally clean. Thoughtfully made.',
  domain: 'nlcstpete.com',
  url: 'https://nlcstpete.com',
  ogImage: '/images/og-default.jpg',
  founded: 'December 2025',
  location: 'St. Petersburg, FL',
  serviceArea: 'Greater Tampa Bay',

  contact: {
    // No phone number is published right now: contact is email and the forms.
    email: 'questions@nlcstpete.com',
    address: 'St. Petersburg, FL 33713',
    hours: 'Mon\u2013Sat, 7 AM \u2013 6 PM',
  },

  hours: {
    weekday: 'Mon\u2013Fri: 8:00 am\u20136:00 pm',
    saturday: 'Saturday: 9:00 am\u20134:00 pm',
    sunday: 'Sunday: Closed',
    structured: 'Mo-Fr 08:00-18:00, Sa 09:00-16:00',
  },

  social: {
    yelp: 'https://www.yelp.com/biz/new-leaf-cleaning-st-petersburg',
    facebook: 'https://www.facebook.com/newleafcleaningservice',
    instagram: 'https://www.instagram.com/newleafcleaningservice',
    google: 'https://maps.app.goo.gl/VVCfSo87AAuCpYYM6',
  },

  geo: {
    lat: 27.793,
    lng: -82.6659,
  },

  // Legacy stats \u2014 kept for backward-compat with existing routes
  stats: {
    yelpRating: '5.0',
    yelpReviews: 4,
    responseTime: '< 10 min',
    communityGiving: '10%',
  },

  // reviews block used by new marketing routes
  reviews: {
    yelpRating: 5.0,
    yelpCount: 4,
    yelpUrl: 'https://www.yelp.com/biz/new-leaf-cleaning-st-petersburg',
  },

  // New marketing nav (Forest & Brass design)
  navLinks: [
    { label: 'Services',      href: '/services' },
    { label: 'Pricing',       href: '/pricing' },
    { label: 'About',         href: '/about' },
    { label: 'Reviews',       href: '/reviews' },
    { label: 'Service areas', href: '/service-areas' },
  ],

  footerLinks: {
    quickLinks: [
      { label: 'Home', href: '/' },
      { label: 'Services', href: '/services' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Get a Quote', href: '/quote' },
      { label: 'Service Areas', href: '/service-areas' },
      { label: 'About Us', href: '/about' },
      { label: 'Policies', href: '/policies' },
    ],
    portalLinks: [
      { label: 'Client Portal', href: '/client/login' },
      { label: 'Admin Login', href: '/admin/login' },
      { label: 'Employee Login', href: '/team/login' },
    ],
    legalLinks: [
      { label: 'Privacy Policy', href: '/policies#privacy' },
      { label: 'Terms of Service', href: '/policies#liability' },
    ],
  },
} as const

// ---------------------------------------------------------------------------
// Pricing — DERIVED, never hand-set.
//
// These values come from the ONE pricing engine in src/shared/pricing.ts, via
// its unseeded defaults. The marketing site cannot read admin_settings (it is
// static at module scope), so it shows the default card — but the math and the
// numbers are the engine's, not a second copy.
//
// Before 2026-09-04 this file held its own hardcoded flat rate table and its
// own `sqft * rate` calculator. It silently diverged from the real engine and
// quoted prices the booking flow would not honour. Do not reintroduce that.
// ---------------------------------------------------------------------------
import {
  calculatePrice,
  RATE_CARD,
  type PricingInput,
} from '@shared/pricing';

/**
 * The one rate card, imported not constructed. This used to be
 * `loadRatesFromSettings({})` — one of four call sites that each built the same
 * object from the same defaults, which is four chances to build it differently.
 */
const RATES = RATE_CARD;

/** The six services the marketing site talks about. */
export type ServiceKey =
  | 'weekly'
  | 'biweekly'
  | 'every4'
  | 'maintenance'
  | 'fulldeep'
  | 'moveout';

const SERVICE_INPUT: Record<ServiceKey, Omit<PricingInput, 'sqft'>> = {
  weekly:      { furnishingState: 'furnished',   serviceType: 'recurring_maintenance', recurrenceFrequency: 'weekly' },
  biweekly:    { furnishingState: 'furnished',   serviceType: 'recurring_maintenance', recurrenceFrequency: 'biweekly' },
  every4:      { furnishingState: 'furnished',   serviceType: 'recurring_maintenance', recurrenceFrequency: 'every_4_weeks' },
  maintenance: { furnishingState: 'furnished',   serviceType: 'one_time' },
  fulldeep:    { furnishingState: 'furnished',   serviceType: 'deep_clean' },
  moveout:     { furnishingState: 'unfurnished', serviceType: 'one_time' },
};

/**
 * Headline "from $X/sqft" rates — the BAND 1 rate for each service, i.e. what
 * the first 1,500 sqft costs. Larger homes pay less per sqft than this; use
 * calcPrice() for any real number shown to a client.
 */
export const PRICING = {
  weeklyRate:      RATES.recurringWeekly.band1,
  biweeklyRate:    RATES.recurringBiweekly.band1,
  monthly4Rate:    RATES.recurring4Weeks.band1,
  maintenanceRate: RATES.furnishedMaintenance.band1,
  fullDeepRate:    RATES.furnishedDeep.band1,
  moveOutRate:     RATES.unfurnishedPerSqft,
  deepUpgradeRate: RATES.deepUpgradePerSqft,
  minimum:         RATES.jobMinimum,
  deepMinimum:     RATES.deepCleanMinimum,
  band1MaxSqft:    RATES.band1MaxSqft,
  cardMaxSqft:     RATES.cardMaxSqft,
} as const;

/** Price a service in whole DOLLARS, using the real banded engine. */
export function calcPrice(sqft: number, service: ServiceKey): number {
  if (!sqft || sqft < 1) return 0;
  const { totalPrice } = calculatePrice(RATES, { sqft, ...SERVICE_INPUT[service] });
  return Math.round(totalPrice / 100);
}

/** True when the home is past the published card and needs a walk-through. */
export function needsSiteVisit(sqft: number): boolean {
  return sqft > RATES.cardMaxSqft;
}

export function formatPrice(dollars: number): string {
  return '$' + dollars.toLocaleString();
}
